/**
 * Sessão do operador.
 *
 * O token vale 60 min e é renovável por até 24 h desde o login (F7.2). Quem
 * agenda a renovação é o `useSessionKeeper`; aqui fica só o estado.
 *
 * O token vive em `sessionStorage`, não em `localStorage`: fechou a aba, acabou.
 * É credencial de operador em máquina possivelmente compartilhada.
 *
 * ## O papel é guardado E reconferido
 *
 * O papel decide quais abas existem (Usuários, Vínculos, Tipos de alerta). Ele
 * vinha só da resposta do login e morava na memória — então um **F5 com a
 * sessão válida** deixava `role = null` e as abas de administrador sumiam da
 * tela sem nada ter mudado no servidor. Era isso, e não permissão, que fazia o
 * cadastro de usuários "não existir" depois de recarregar a página.
 *
 * Agora o papel é gravado junto do token (para a tela não piscar sem as abas) e
 * **reconferido no `/auth/me`** assim que o painel sobe. A cópia guardada pode
 * estar velha — alguém pode ter sido promovido ou rebaixado com a aba aberta —,
 * e quem manda é sempre o servidor. A trava de verdade continua sendo a da API:
 * esconder a aba é conveniência, não segurança.
 */
import { create } from "zustand";
import { api, ApiError } from "../lib/api.ts";

const STORAGE_KEY = "mt.session";

interface StoredSession {
  token: string;
  expiresAt: number;
  /** Último papel conhecido. Conveniência: o `/auth/me` confirma logo depois. */
  role?: string | null;
}

interface AuthState {
  token: string | null;
  expiresAt: number | null;
  role: string | null;
  /** Quem é o usuário logado. Vem do `/auth/me`, não do login. */
  userId: string | null;
  status: "idle" | "loading" | "error";
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  refresh: () => Promise<boolean>;
  /** Reconfere no servidor quem é o dono do token restaurado. */
  hydrate: () => Promise<void>;
  logout: () => void;
}

function read(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    // Token já vencido no armazenamento não é sessão: é uma tela de erro
    // esperando acontecer no primeiro clique.
    return parsed.expiresAt > Date.now() ? parsed : null;
  } catch {
    return null;
  }
}

function write(session: StoredSession | null): void {
  try {
    if (session) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* navegador com armazenamento bloqueado: a sessão só não sobrevive ao F5 */
  }
}

const restored = read();

export const useAuthStore = create<AuthState>((set, get) => ({
  token: restored?.token ?? null,
  expiresAt: restored?.expiresAt ?? null,
  role: restored?.role ?? null,
  // Não é persistido: `hydrate` o traz do servidor a cada subida, e ele serve
  // só para a tela não oferecer ações que o próprio dono não pode fazer.
  userId: null,
  status: "idle",
  error: null,

  login: async (email, password) => {
    set({ status: "loading", error: null });
    try {
      const { token, role, expiresInSeconds } = await api.login(email, password);
      const expiresAt = Date.now() + expiresInSeconds * 1000;
      write({ token, expiresAt, role });
      set({ token, expiresAt, role, status: "idle", error: null });
    } catch (error) {
      const message =
        error instanceof ApiError && error.status === 401
          ? "E-mail ou senha inválidos."
          : error instanceof ApiError && error.status === 0
            ? "Não foi possível falar com a API. Verifique a rede."
            : "Não foi possível entrar. Tente novamente.";
      set({ status: "error", error: message });
      throw error;
    }
  },

  /** Devolve `false` quando a sessão acabou de vez — aí é login de novo. */
  refresh: async () => {
    const { token } = get();
    if (!token) return false;
    try {
      const { token: next, expiresInSeconds } = await api.refresh(token);
      const expiresAt = Date.now() + expiresInSeconds * 1000;
      // O papel vai junto: `write` substitui o registro inteiro, e omiti-lo
      // apagaria o papel a cada renovação — as abas sumiriam sozinhas no meio
      // do turno, uma hora depois do login.
      write({ token: next, expiresAt, role: get().role });
      set({ token: next, expiresAt });
      return true;
    } catch {
      // Passou do teto de 24 h, ou o usuário foi desativado. Nos dois casos o
      // caminho é o mesmo, e insistir só produziria um laço de 401.
      get().logout();
      return false;
    }
  },

  /**
   * Confirma o papel com o servidor depois de restaurar a sessão.
   *
   * Falha de rede não desloga: o painel segue com o papel guardado e tenta de
   * novo na próxima subida. Só 401 encerra — aí o token não vale mais mesmo.
   */
  hydrate: async () => {
    const { token } = get();
    if (!token) return;
    try {
      const me = await api.me(token);
      set({ role: me.role, userId: me.userId ?? null });
      const { expiresAt } = get();
      if (expiresAt) write({ token, expiresAt, role: me.role });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) get().logout();
    }
  },

  logout: () => {
    write(null);
    set({ token: null, expiresAt: null, role: null, userId: null, status: "idle", error: null });
  },
}));
